import { ref, onUnmounted, nextTick } from 'vue'

/**
 * Tracks which list items (identified by their `data-i` attribute) are currently
 * visible inside the app's scroll container, and notifies a callback whenever the
 * visible set changes. Used to lazily fetch odds/selections only for on-screen events.
 *
 * Why this exists / what it fixes:
 * The app scrolls inside `<main class="pageView" data-scroll-root>` (the window itself
 * never scrolls — the outer shell is `overflow-hidden`). The previous implementation
 * created `IntersectionObserver`s with `root: null`, i.e. observing against the visual
 * viewport instead of the real scroll container. Chromium tolerates this, but WebKit
 * (Safari) does not reliably compute or recompute intersection against the viewport when
 * the true scroller is a nested `overflow:auto` element. The result: the initial
 * intersection callback could report nothing visible, so odds were never fetched while
 * the event list (rendered straight from the store, needing no observer) still showed.
 *
 * This composable fixes that by:
 *  1. Resolving the actual scroll container as the observer `root` (so initial + scroll
 *     intersection fire correctly in every browser, Safari included).
 *  2. Observing after paint (nextTick + rAF) so layout is settled before measuring.
 *  3. Seeding visibility with a direct geometry check (getBoundingClientRect) as a
 *     belt-and-suspenders fallback, guaranteeing that currently on-screen events load
 *     their odds even if the browser delays or drops the first IO callback.
 *
 * @param {(ids: number[]) => void} onVisibleChange Called with all currently-visible ids.
 * @param {object} options
 * @param {string} options.selector CSS selector for the observed list items.
 * @param {number} [options.threshold=0]
 * @param {string} [options.rootMargin='300px 0px'] Pre-loads odds slightly before scroll-in.
 * @param {Element|null} [options.root] Explicit root; otherwise auto-detected.
 */
export function useVisibleItems(onVisibleChange, options = {}) {
  const {
    selector,
    threshold = 0,
    rootMargin = '300px 0px',
  } = options

  const visibleIds = ref(new Set())
  let observer = null
  let scheduled = false

  const findScrollRoot = (el) => {
    if (options.root !== undefined) return options.root
    const explicit = el?.closest?.('[data-scroll-root]')
    if (explicit) return explicit
    let node = el?.parentElement
    while (node && node !== document.body && node !== document.documentElement) {
      const oy = getComputedStyle(node).overflowY
      if (oy === 'auto' || oy === 'scroll') return node
      node = node.parentElement
    }
    return null
  }

  const notify = () => {
    if (typeof onVisibleChange === 'function') {
      onVisibleChange(Array.from(visibleIds.value))
    }
  }

  const handleEntries = (entries) => {
    let changed = false
    for (const entry of entries) {
      const id = Number(entry.target.dataset.i)
      if (Number.isNaN(id)) continue
      if (entry.isIntersecting) {
        if (!visibleIds.value.has(id)) { visibleIds.value.add(id); changed = true }
      } else if (visibleIds.value.has(id)) {
        visibleIds.value.delete(id)
        changed = true
      }
    }
    if (changed) notify()
  }

  // Direct geometry fallback: pick up items already on screen without waiting for the
  // observer's first callback. Margin mirrors rootMargin so behaviour stays consistent.
  const seedVisible = (root, els) => {
    const rect = root
      ? root.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight || document.documentElement.clientHeight }
    const margin = 300
    let changed = false
    for (const el of els) {
      const id = Number(el.dataset.i)
      if (Number.isNaN(id)) continue
      const r = el.getBoundingClientRect()
      const onScreen = r.bottom > rect.top - margin && r.top < rect.bottom + margin
      if (onScreen && !visibleIds.value.has(id)) {
        visibleIds.value.add(id)
        changed = true
      }
    }
    if (changed) notify()
  }

  const observe = () => {
    if (scheduled) return
    scheduled = true
    nextTick(() => {
      requestAnimationFrame(() => {
        scheduled = false
        const els = Array.from(document.querySelectorAll(selector))
        if (!els.length) return
        const root = findScrollRoot(els[0])
        if (observer) observer.disconnect()
        observer = new IntersectionObserver(handleEntries, { root, threshold, rootMargin })
        els.forEach((el) => observer.observe(el))
        seedVisible(root, els)
      })
    })
  }

  const reset = () => {
    visibleIds.value.clear()
    if (observer) observer.disconnect()
    observer = null
  }

  onUnmounted(() => {
    if (observer) observer.disconnect()
    observer = null
  })

  return { visibleIds, observe, reset }
}
